import { logOperationalEvent } from 'saddlebag-logger';

import { COMPONENT, CMP_INITIALISED } from './constants/events';
import {
  registerIsPurposeScopedIdServiceEnabled,
  registerIsCookieVersionRollbackEnabled,
  maybeDowngradeCookieVersion,
} from './cookie-version-utils';
import { isBrowserEnvironment } from './isomorphic';
import { getKetchCookieValue, hasActiveConsent } from './ketch-cookie-utils';

import type { CMPSource, TrackingPreferencesConfig } from './ketch-types';

// Module-level state (singleton)
let cmpSource: CMPSource = 'skyscanner'; // Default for backward compatibility
let ketchScriptUrl: string | undefined;
let consumer = '';
let cachedWillShowBanner: boolean | undefined;

// Configurable Ketch keys with defaults
const DEFAULT_CONSENT_COOKIE_NAME = '_swb_consent_';
const DEFAULT_TCF_STORAGE_KEY = '_ketch_tcf_tcdata_v1_';
let consentCookieName: string = DEFAULT_CONSENT_COOKIE_NAME;
let tcfStorageKey: string = DEFAULT_TCF_STORAGE_KEY;

// Purpose-scoped ID service configuration
const DEFAULT_PURPOSE_SCOPED_ID_SERVICE_LEGACY_CMP_URL =
  '/g/purpose-scoped-id-service/identifiers/refresh-legacy';
const DEFAULT_PURPOSE_SCOPED_ID_SERVICE_KETCH_CMP_URL =
  '/g/purpose-scoped-id-service/identifiers/refresh';
const DEFAULT_CONSENT_ID_SERVICE_URL =
  '/g/purpose-scoped-id-service/identifiers/consent-id';
let purposeScopedIdServiceEnabled = false;
let purposeScopedIdServiceLegacyCmpUrl: string =
  DEFAULT_PURPOSE_SCOPED_ID_SERVICE_LEGACY_CMP_URL;
let purposeScopedIdServiceKetchCmpUrl: string =
  DEFAULT_PURPOSE_SCOPED_ID_SERVICE_KETCH_CMP_URL;
let consentIdServiceUrl: string = DEFAULT_CONSENT_ID_SERVICE_URL;
let consentIdServiceEnabled = false;
let cookieVersionRollbackEnabled = false;
let enablePurposeScopedIdExistingGDPRUsers = false;
let enablePurposeScopedIdNonRegulatedMarkets = false;
let ketchPsidSyncEnabled = false;
let legacyConsentSyncEnabled = false;

function evaluateWillShowBanner(): boolean {
  const cookieValue = getKetchCookieValue(consentCookieName);
  if (!cookieValue) {
    return true;
  }
  return !hasActiveConsent(cookieValue);
}

const KETCH_ORIGINS = ['https://eu.ketchcdn.com', 'https://cdn.ketchjs.com'];

function injectResourceHints(): void {
  const { head } = document;
  for (const origin of KETCH_ORIGINS) {
    if (!head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
      const preconnect = document.createElement('link');
      preconnect.rel = 'preconnect';
      preconnect.href = origin;
      preconnect.crossOrigin = '';
      head.appendChild(preconnect);
    }
  }
}

/**
 * Initialize tracking preferences configuration.
 * Sets the CMP source and optional Ketch script URL.
 *
 * @param config - Configuration object
 *
 * Behaviour:
 * - Default CMP source is 'skyscanner' if never called (backward compatible)
 * - Multiple calls: last call wins (no error/warning)
 * - Script URL is stored for later use by loadCMPSDK
 */
export function initTrackingPreferences(
  config: TrackingPreferencesConfig,
): void {
  if (!config.consumer) {
    throw new Error('consumer is required in TrackingPreferencesConfig');
  }

  cmpSource = config.enableKetchCMP ? 'ketch' : 'skyscanner';
  consumer = config.consumer;
  ketchScriptUrl = config.ketchScriptUrl;
  consentCookieName =
    config.ketchConsentCookieName ?? DEFAULT_CONSENT_COOKIE_NAME;
  tcfStorageKey = config.ketchTCFStorageKey ?? DEFAULT_TCF_STORAGE_KEY;
  purposeScopedIdServiceEnabled = config.enablePurposeScopedIdService === true;
  purposeScopedIdServiceLegacyCmpUrl =
    config.purposeScopedIdServiceLegacyCmpUrl ??
    DEFAULT_PURPOSE_SCOPED_ID_SERVICE_LEGACY_CMP_URL;
  purposeScopedIdServiceKetchCmpUrl =
    config.purposeScopedIdServiceKetchCmpUrl ??
    DEFAULT_PURPOSE_SCOPED_ID_SERVICE_KETCH_CMP_URL;
  cookieVersionRollbackEnabled = config.enableCookieVersionRollback === true;
  enablePurposeScopedIdExistingGDPRUsers =
    config.enablePurposeScopedIdExistingGDPRUsers === true;
  enablePurposeScopedIdNonRegulatedMarkets =
    config.enablePurposeScopedIdNonRegulatedMarkets === true;
  ketchPsidSyncEnabled = config.enableKetchPsidSync === true;
  legacyConsentSyncEnabled = config.enableLegacyConsentSync === true;
  consentIdServiceEnabled = config.enableConsentIdService === true;
  consentIdServiceUrl =
    config.consentIdServiceUrl ?? DEFAULT_CONSENT_ID_SERVICE_URL;

  // Proactively check if cookie version needs to be downgraded (rollback scenario).
  // This handles the case where the flag is turned off after a user got a higher version.
  // Only runs in browser context (maybeDowngradeCookieVersion checks for cookie existence).
  if (isBrowserEnvironment()) {
    if (isKetchMode()) {
      cachedWillShowBanner = evaluateWillShowBanner();
      injectResourceHints();
    } else {
      cachedWillShowBanner = undefined;
    }

    logOperationalEvent({
      eventName: CMP_INITIALISED,
      component: COMPONENT,
      cmpSource,
      consumer,
    });

    maybeDowngradeCookieVersion();
  }
}

/**
 * Get the current CMP source.
 *
 * @returns The current CMP source ('skyscanner' or 'ketch')
 */
export function getCMPSource(): CMPSource {
  return cmpSource;
}

/**
 * Check if Ketch CMP mode is active.
 * Helper to reduce boilerplate in routing checks.
 *
 * @returns true if Ketch CMP is enabled, false otherwise
 */
export function isKetchMode(): boolean {
  return cmpSource === 'ketch';
}

/**
 * Returns the init-time cached banner decision, or undefined if not yet evaluated.
 *
 * @returns cached boolean or undefined
 */
export function getCachedWillShowBanner(): boolean | undefined {
  return cachedWillShowBanner;
}

/**
 * Re-evaluates the willShowBanner decision from the current cookie state
 * and updates the cache. Call this when consent changes (e.g. user accepts banner)
 * so subsequent reads of willShowConsentBanner() reflect the new state.
 */
export function invalidateWillShowBannerCache(): void {
  if (
    !isBrowserEnvironment() ||
    !isKetchMode() ||
    cachedWillShowBanner === undefined
  ) {
    return;
  }
  cachedWillShowBanner = evaluateWillShowBanner();
}

/**
 * Get the stored Ketch script URL.
 *
 * @returns The Ketch script URL if set, undefined otherwise
 */
export function getKetchScriptUrl(): string | undefined {
  return ketchScriptUrl;
}

/**
 * Get the configured consent cookie name.
 *
 * @returns The consent cookie name (default: '_swb_consent_')
 */
export function getConsentCookieName(): string {
  return consentCookieName;
}

/**
 * Get the configured TCF localStorage key.
 *
 * @returns The TCF storage key (default: '_ketch_tcf_tcdata_v1_')
 */
export function getTCFStorageKey(): string {
  return tcfStorageKey;
}

/**
 * Get the consumer identifier for logging.
 *
 * @returns The consumer identifier (e.g., 'banana', 'falcon')
 */
export function getConsumer(): string {
  return consumer;
}

/**
 * Check if purpose-scoped ID service is enabled.
 *
 * @returns true if the service is enabled, false otherwise
 */
export function isPurposeScopedIdServiceEnabled(): boolean {
  return purposeScopedIdServiceEnabled;
}

/**
 * Check if cookie version rollback is enabled.
 * When true, users with a higher cookie version than the target version
 * will have their cookie downgraded. This is for rollback scenarios only.
 *
 * @returns true if rollback is enabled, false otherwise
 */
export function isCookieVersionRollbackEnabled(): boolean {
  return cookieVersionRollbackEnabled;
}

/**
 * Check if purpose-scoped ID provisioning for existing GDPR users is enabled.
 * When true, migrateExistingUserTracking() will provision tracking IDs
 * for existing v3 cookie users with consent in regulated markets.
 *
 * @returns true if enabled, false otherwise
 */
export function isPurposeScopedIdExistingGDPRUsersEnabled(): boolean {
  return enablePurposeScopedIdExistingGDPRUsers;
}

/**
 * Check if purpose-scoped ID provisioning for non-regulated markets is enabled.
 * When true, provisionNonRegulatedMarketTracking() will provision tracking IDs
 * for users in non-regulated markets.
 *
 * @returns true if enabled, false otherwise
 */
export function isPurposeScopedIdNonRegulatedMarketsEnabled(): boolean {
  return enablePurposeScopedIdNonRegulatedMarkets;
}

/**
 * Check if Ketch PSID sync is enabled.
 * When true, the _swb_consent_ cookie is sent to the purpose-scoped-id-service
 * when Ketch's cachedConsent event fires.
 *
 * @returns true if Ketch PSID sync is enabled, false otherwise
 */
export function isKetchPsidSyncEnabled(): boolean {
  return ketchPsidSyncEnabled;
}

// Register the functions with cookie-version-utils to break circular dependency
registerIsPurposeScopedIdServiceEnabled(isPurposeScopedIdServiceEnabled);
registerIsCookieVersionRollbackEnabled(isCookieVersionRollbackEnabled);

/**
 * Get the purpose-scoped ID service legacy CMP endpoint URL.
 *
 * @returns The endpoint URL
 */
export function getPurposeScopedIdServiceLegacyCmpUrl(): string {
  return purposeScopedIdServiceLegacyCmpUrl;
}

/**
 * Get the purpose-scoped ID service Ketch CMP endpoint URL.
 *
 * @returns The endpoint URL
 */
export function getPurposeScopedIdServiceKetchCmpUrl(): string {
  return purposeScopedIdServiceKetchCmpUrl;
}

/**
 * Check if legacy consent sync is enabled (bidirectional gdpr ↔ Ketch sync).
 *
 * @returns true if sync is enabled, false otherwise
 */
export function isLegacyConsentSyncEnabled(): boolean {
  return legacyConsentSyncEnabled;
}

export function isConsentIdServiceEnabled(): boolean {
  return consentIdServiceEnabled;
}

export function getConsentIdServiceUrl(): string {
  return consentIdServiceUrl;
}

/**
 * Reset CMP state to defaults.
 * FOR TESTING ONLY - do not use in production code.
 */
export function resetCMPState(): void {
  cmpSource = 'skyscanner';
  consumer = '';
  ketchScriptUrl = undefined;
  consentCookieName = DEFAULT_CONSENT_COOKIE_NAME;
  tcfStorageKey = DEFAULT_TCF_STORAGE_KEY;
  purposeScopedIdServiceEnabled = false;
  purposeScopedIdServiceLegacyCmpUrl =
    DEFAULT_PURPOSE_SCOPED_ID_SERVICE_LEGACY_CMP_URL;
  purposeScopedIdServiceKetchCmpUrl =
    DEFAULT_PURPOSE_SCOPED_ID_SERVICE_KETCH_CMP_URL;
  cookieVersionRollbackEnabled = false;
  enablePurposeScopedIdExistingGDPRUsers = false;
  enablePurposeScopedIdNonRegulatedMarkets = false;
  ketchPsidSyncEnabled = false;
  legacyConsentSyncEnabled = false;
  consentIdServiceEnabled = false;
  consentIdServiceUrl = DEFAULT_CONSENT_ID_SERVICE_URL;
  cachedWillShowBanner = undefined;
}
